/**
 *  @class
 *  @function Timeline
 */

if (!customElements.get('time-line')) {
  class Timeline extends HTMLElement {
    constructor() {
      super();

      this.id = this.dataset.id;
      this.scroller = this.querySelector('.timeline--scroll-container');
      this.inner = this.querySelector('.timeline--scroll-inner');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.scrollSet = false;
      this.items = this.querySelectorAll('.timeline--slide');
      this.labels = this.querySelectorAll('.timeline--scroll-pagination li');
      this.prevButton = this.querySelector('.timeline-nav-arrow--prev');
      this.nextButton = this.querySelector('.timeline-nav-arrow--next');
      this.activeIndex = 0;
      this.resizeTimer = null;
      this.scrollTimer = null;
      this.isAutoScrolling = false;
      this.autoScrollTargetIndex = null;

      if (this.animations_enabled) {
        this.scrollSet = gsap.quickSetter('#time-line-progress-' + this.id, 'scaleX');
      }

      this.onScrollerScroll = this.onScrollerScroll.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
    }

    connectedCallback() {
      this.labels.forEach((item) => {
        this.setLabelPosition(item);
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'button');
      });

      this.setActiveLabel(this.activeIndex);
      this.updateActiveState();
      this.updateArrowState();

      if (this.scroller) {
        this.scroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goToSlide(this.activeIndex - 1);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goToSlide(this.activeIndex + 1);
        });
      }

      this.labels.forEach((label, index) => {
        label.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goToSlide(index);
        });

        label.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.goToSlide(index);
          }
        });

        const innerLabel = label.querySelector('.timeline--label');
        const innerDot = label.querySelector('.timeline--dot');

        if (innerLabel) {
          innerLabel.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.goToSlide(index);
          });
        }

        if (innerDot) {
          innerDot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.goToSlide(index);
          });
        }
      });

      window.addEventListener('resize', this.onWindowResize);
    }

    disconnectedCallback() {
      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this.onScrollerScroll);
      }

      window.removeEventListener('resize', this.onWindowResize);

      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
    }

onScrollerScroll() {
  if (this.animations_enabled) {
    this.onScroll();
  }

  if (this.isAutoScrolling) {
    return;
  }

  if (this.scrollTimer) {
    clearTimeout(this.scrollTimer);
  }

  this.scrollTimer = setTimeout(() => {
    this.updateActiveState();
  }, 80);
}

    onWindowResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = setTimeout(() => {
        this.labels.forEach((item) => {
          this.setLabelPosition(item);
        });

        this.updateActiveState();
        this.updateArrowState();

        if (this.animations_enabled) {
          this.onScroll();
        }
      }, 100);
    }

    setLabelPosition(item) {
      if (!this.inner || !this.labels.length || !this.items.length) return;

      const contentWidth = this.inner.offsetWidth;
      const arr = Array.prototype.slice.call(this.labels);
      const i = arr.indexOf(item);

      if (i < 0 || !this.items[i] || !contentWidth) return;

      const itemWidth = this.items[i].offsetWidth;
      const minDesktopWidth = window.innerWidth >= 768 ? 120 : 0;

      if (window.innerWidth >= 768) {
        item.style.flex = `1 1 ${Math.max(itemWidth, minDesktopWidth)}px`;
      } else {
        item.style.flex = (itemWidth / contentWidth);
      }
    }

    onScroll() {
      if (!this.scrollSet || !this.labels.length) return;

      const activeLabel = this.labels[this.activeIndex];
      if (!activeLabel) return;

      const dot = activeLabel.querySelector('.timeline--dot');
      const pagination = this.querySelector('.timeline--scroll-pagination ul');

      if (!dot || !pagination) return;

      const paginationRect = pagination.getBoundingClientRect();
      const dotRect = dot.getBoundingClientRect();

      const dotCenter = (dotRect.left - paginationRect.left) + (dotRect.width / 2);
      const progressPercent = paginationRect.width > 0 ? dotCenter / paginationRect.width : 0;

      this.scrollSet(progressPercent);
    }

    setActiveLabel(index) {
      this.activeIndex = index;

      this.labels.forEach((label, labelIndex) => {
        const isActive = labelIndex === index;
        label.classList.toggle('is-active', isActive);
        label.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.updateArrowState();

      if (this.animations_enabled) {
        this.onScroll();
      }
    }

updateActiveState() {
  if (!this.items.length || !this.scroller) return;

  const scrollLeft = this.scroller.scrollLeft;
  const maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth;

  let closestIndex = 0;
  let closestDistance = Infinity;

  this.items.forEach((item, index) => {
    const itemOffset = Math.min(item.offsetLeft, maxScrollLeft);
    const distance = Math.abs(itemOffset - scrollLeft);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  const currentOffset = Math.min(this.items[this.activeIndex].offsetLeft, maxScrollLeft);
  const currentDistance = Math.abs(currentOffset - scrollLeft);

  if (currentDistance <= closestDistance + 4) {
    this.setActiveLabel(this.activeIndex);
  } else {
    this.setActiveLabel(closestIndex);
  }
}

    updateArrowState() {
      if (this.prevButton) {
        this.prevButton.disabled = this.activeIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.activeIndex === this.items.length - 1;
      }
    }

goToSlide(index) {
  if (index < 0 || index >= this.items.length || !this.scroller) return;

  this.isAutoScrolling = true;
  this.autoScrollTargetIndex = index;
  this.setActiveLabel(index);

  const item = this.items[index];
  const maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth;

  let targetLeft = item.offsetLeft - ((this.scroller.clientWidth - item.offsetWidth) / 2);
  targetLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

  this.scroller.scrollTo({
    left: targetLeft,
    behavior: 'smooth'
  });

  if (this.scrollTimer) {
    clearTimeout(this.scrollTimer);
  }

  this.scrollTimer = setTimeout(() => {
    this.isAutoScrolling = false;
    this.setActiveLabel(index);

    if (this.animations_enabled) {
      this.onScroll();
    }
  }, 500);
}
  }

  customElements.define('time-line', Timeline);
}